/*
 * The mapping between what is on screen and what is in the address bar.
 *
 * Kept separate from App.js because it is the part that is easy to get subtly
 * wrong and impossible to eyeball: whether Back returns to the previous view
 * or leaves the site depends on a couple of small decisions here, and those
 * are worth testing rather than clicking through.
 *
 * A "view" is the plain object { tab, creatorId, pairWith, videoCreators }.
 */

export const DEFAULT_TAB = 'search';

/** The query string for a view. Returns '' for the default view. */
export function searchForView(view) {
  const p = new URLSearchParams();
  const { tab, creatorId, pairWith, videoCreators } = view || {};

  if (tab === 'author-page') {
    // A profile is identified by its creator and nothing else. Without one
    // there is no page to describe, so fall back rather than emitting
    // "tab=author-page", which would restore a profile view with nobody in it.
    if (!creatorId) return '';
    p.set('creator', String(creatorId));
    if (pairWith) p.set('with', String(pairWith));
  } else if (tab === 'find-videos') {
    p.set('tab', 'videos');
    if (videoCreators && videoCreators.length) p.set('videos', videoCreators.join(','));
  } else if (tab && tab !== DEFAULT_TAB) {
    p.set('tab', tab);
  }
  return p.toString();
}

/**
 * The view a query string describes.
 *
 * `isKnownId` rejects ids that are not in the loaded data, so a stale or
 * mistyped link falls back to something sensible instead of opening a blank
 * profile.
 */
export function viewFromSearch(search, isKnownId = () => true) {
  const p = new URLSearchParams(search || '');
  const num = (v) => {
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : null;
  };

  const creatorId = num(p.get('creator'));
  if (creatorId && isKnownId(creatorId)) {
    const withId = num(p.get('with'));
    return {
      tab: 'author-page',
      creatorId,
      pairWith: withId && isKnownId(withId) ? withId : null,
      videoCreators: [],
    };
  }

  const tab = p.get('tab');
  if (tab === 'videos') {
    return {
      tab: 'find-videos',
      creatorId: null,
      pairWith: null,
      videoCreators: (p.get('videos') || '')
        .split(',')
        .map(num)
        .filter((n) => n && isKnownId(n)),
    };
  }

  return {
    // "author-page" is never a valid tab on its own: reaching here means the
    // creator was missing or unknown, so there is nothing to show.
    tab: tab && tab !== DEFAULT_TAB && tab !== 'author-page' ? tab : DEFAULT_TAB,
    creatorId: null,
    pairWith: null,
    videoCreators: [],
  };
}

/**
 * Whether moving between two views is NAVIGATION or a REFINEMENT.
 *
 * This is the decision the back button hangs on. Opening a profile or changing
 * tab is going somewhere, and Back should undo it, so it gets a history entry.
 * Narrowing a filter within the view you are already looking at is refining,
 * and pushing an entry for each would bury the page you came from under a
 * dozen of them.
 */
export function isNavigation(from, to) {
  if (!from) return true;
  if (from.tab !== to.tab) return true;
  if ((from.creatorId || null) !== (to.creatorId || null)) return true;
  return false;
}
